import React from 'react';
import { FaCheckCircle, FaClock, FaTimesCircle, FaEnvelope } from 'react-icons/fa';

const EmailTable = ({ 
  emails, 
  loading, 
  pagination, 
  setPagination, 
  handleStatusChange, 
  handleDeleteEmail, 
  formatDate 
}) => {
  const getStatusIcon = (status) => {
    switch (status) {
      case 'contacted':
        return <FaCheckCircle className="text-green-500" />;
      case 'responded':
        return <FaEnvelope className="text-blue-500" />;
      case 'closed':
        return <FaTimesCircle className="text-red-500" />;
      case 'unsubscribed':
        return <FaTimesCircle className="text-red-500" />;
      default:
        return <FaClock className="text-yellow-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Bekliyor';
      case 'contacted':
        return 'Email Gönderildi';
      case 'responded':
        return 'Yanıtlandı';
      case 'closed':
        return 'Kapatıldı';
      case 'unsubscribed':
        return 'Unsubscribed';
      default:
        return 'Bilinmiyor';
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-2 text-gray-300">Loading emails...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full divide-y divide-gray-700">
          <thead className="bg-gray-700">
            <tr>
              <th className="px-2 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider w-2/5">
                Uygulama
              </th>
              <th className="px-2 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider w-1/5">
                Developer
              </th>
              <th className="px-2 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider w-1/6">
                Durum
              </th>
              <th className="hidden lg:table-cell px-2 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider w-1/6">
                Tarih
              </th>
              <th className="px-2 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider w-1/6">
                İşlemler
              </th>
            </tr>
          </thead>
          <tbody className="bg-gray-800 divide-y divide-gray-700">
            {emails.map((email) => (
              <tr key={email._id} className="hover:bg-gray-700">
                <td className="px-2 py-2 text-left">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-8 w-8 mr-3">
                      {email.appLogo ? (
                        <img
                          className="h-8 w-8 rounded"
                          src={email.appLogo}
                          alt={email.appName}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className="h-8 w-8 rounded bg-gray-600 flex items-center justify-center text-white text-xs font-bold"
                        style={{ display: email.appLogo ? 'none' : 'flex' }}
                      >
                        {email.appName ? email.appName.charAt(0).toUpperCase() : 'A'}
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <a
                        href={email.playStoreUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium text-white hover:text-blue-400 cursor-pointer"
                      >
                        {email.appName}
                      </a>
                      <div className="text-xs text-gray-300">{email.appPackageName}</div>
                      {email.downloadCount && (
                        <div className="text-xs text-green-400">
                          {email.downloadCount.includes('Downloads') || email.downloadCount.includes('downloads') ? 
                            email.downloadCount : 
                            `${email.downloadCount} indirme`
                          }
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-2 py-2 text-left">
                  <div>
                    <div className="text-sm font-medium text-white">{email.developerName}</div>
                    <div className="text-sm text-gray-300">{email.developerEmail}</div>
                    {email.companyName && (
                      <div className="text-xs text-gray-400">{email.companyName}</div>
                    )}
                  </div>
                </td>
                <td className="px-2 py-2 text-left">
                  <div className="flex items-center">
                    {getStatusIcon(email.status)}
                    <div className="ml-2 relative group">
                      <span className="text-sm text-white">{getStatusText(email.status)}</span>
                      {email.status === 'unsubscribed' && (
                        <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block bg-gray-900 text-white text-xs rounded-md px-3 py-2 shadow-lg z-10 min-w-48">
                          <div className="text-red-400 font-medium mb-1">
                            {email.unsubscribeReason === 'too-frequent' && 'Çok Sık Email'}
                            {email.unsubscribeReason === 'not-relevant' && 'İlgisiz İçerik'}
                            {email.unsubscribeReason === 'never-signed-up' && 'Hiç Kayıt Olmadım'}
                            {email.unsubscribeReason === 'spam' && 'Spam'}
                            {email.unsubscribeReason === 'other' && 'Diğer'}
                          </div>
                          {email.unsubscribeCustomReason && (
                            <div className="text-gray-300 text-xs">
                              "{email.unsubscribeCustomReason}"
                            </div>
                          )}
                          <div className="text-gray-400 text-xs mt-1">
                            {email.unsubscribedAt && new Date(email.unsubscribedAt).toLocaleDateString('tr-TR', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="hidden lg:table-cell px-2 py-2 text-left">
                  <div className="text-sm text-gray-300">
                    {formatDate(email.createdAt)}
                  </div>
                </td>
                <td className="px-2 py-2 text-left">
                  <div className="flex items-center space-x-1">
                    <select
                      value={email.status}
                      onChange={(e) => handleStatusChange(email._id, e.target.value)}
                      className="px-1 py-1 bg-gray-700 border border-gray-600 text-white text-xs rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="pending">Bekliyor</option>
                      <option value="contacted">Email Gönderildi</option>
                      <option value="responded">Yanıtlandı</option>
                      <option value="closed">Kapatıldı</option>
                      <option value="unsubscribed">Unsubscribed</option>
                    </select>
                    <button
                      onClick={() => handleDeleteEmail(email._id)}
                      className="text-red-400 hover:text-red-300 text-xs"
                    >
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-700 sm:px-6">
          <div className="flex-1 flex justify-center sm:hidden">
            <button
              onClick={() => setPagination({...pagination, current: pagination.current - 1})}
              disabled={pagination.current === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-600 text-sm font-medium rounded-md text-gray-300 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setPagination({...pagination, current: pagination.current + 1})}
              disabled={pagination.current === pagination.pages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-600 text-sm font-medium rounded-md text-gray-300 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="flex-1 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="text-sm text-gray-300">
                Showing <span className="font-medium">{((pagination.current || 1) - 1) * (pagination.limit || 20) + 1}</span> to{' '}
                <span className="font-medium">{Math.min((pagination.current || 1) * (pagination.limit || 20), pagination.total || 0)}</span> of{' '}
                <span className="font-medium">{pagination.total || 0}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => setPagination({...pagination, current: pagination.current - 1})}
                  disabled={pagination.current === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-600 bg-gray-700 text-sm font-medium text-gray-300 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      onClick={() => setPagination({...pagination, current: page})}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        pagination.current === page
                          ? 'z-10 bg-blue-600 border-blue-600 text-white'
                          : 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  onClick={() => setPagination({...pagination, current: pagination.current + 1})}
                  disabled={pagination.current === pagination.pages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-600 bg-gray-700 text-sm font-medium text-gray-300 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailTable;
